import { getFilterDefinition } from '../engine/filterRegistry';
import {
  type Filter,
  type FilterId,
  type FilterSelectionsMap,
  FilterType,
} from '../types/filters';

export interface AdditionalParameter {
  parameterId: string;
  filterId: FilterId;
  filterMap: FilterSelectionsMap;
}

export interface ParameterContext {
  primaryReportParameterId: string | null;
  primaryReportParameterFilterId: FilterId | null;
  hasPrimaryParameterInChart: boolean;
  additionalParameters: AdditionalParameter[];
}

/**
 * Merges two arrays of items by their unique key (id + levelId combination).
 * Used to deduplicate filter selections when merging from multiple parameters.
 */
const mergeUniqueByKey = <T extends { id: string; levelId?: string | null }>(
  a: T[] | undefined,
  b: T[] | undefined
): T[] => {
  const result: T[] = [];
  const seen = new Set<string>();

  const push = (items: T[] | undefined) => {
    (items ?? []).forEach((item) => {
      const key = `${item.levelId ?? ''}:${item.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      result.push(item);
    });
  };

  push(a);
  push(b);
  return result;
};

/**
 * Builds a context object containing information about report parameters
 * and their relationship to plot parameters. This context is used to determine
 * how filter states should be merged from multiple parameters.
 */
export const buildParameterContext = (
  plotParameters: Array<{ parameterId: string }>,
  reportParameters: Array<{
    parameterId: string;
    filterId: FilterId;
    isPrimary?: boolean;
  }>,
  filterState?: { groupMap?: Map<string, FilterSelectionsMap> }
): ParameterContext => {
  const primaryReportParameter =
    reportParameters.find((p) => p.isPrimary) ?? null;
  const primaryReportParameterId = primaryReportParameter?.parameterId ?? null;
  const primaryReportParameterFilterId =
    primaryReportParameter?.filterId ?? null;
  const hasPrimaryParameterInChart = primaryReportParameterId
    ? plotParameters.some((p) => p.parameterId === primaryReportParameterId)
    : true;

  const parameterIdToFilterId = new Map<string, FilterId>(
    reportParameters.map((p) => [p.parameterId, p.filterId])
  );

  const additionalParameters: AdditionalParameter[] = plotParameters
    .map((p) => {
      const filterId = parameterIdToFilterId.get(p.parameterId);
      const filterMap = filterId
        ? filterState?.groupMap?.get(p.parameterId)
        : undefined;
      return filterId && filterMap
        ? { parameterId: p.parameterId, filterId, filterMap }
        : null;
    })
    .filter((p): p is AdditionalParameter => p !== null);

  return {
    primaryReportParameterId,
    primaryReportParameterFilterId,
    hasPrimaryParameterInChart,
    additionalParameters,
  };
};

/**
 * Determines the base filter state for a given filter ID, considering
 * whether the primary report parameter is present in the chart.
 * If the primary parameter is not in the chart, uses its state from
 * additional parameters or falls back to the filter definition.
 */
export const getBaseFilterState = (
  filterId: FilterId,
  currentFilterState: Filter,
  paramContext: ParameterContext
): Filter => {
  // If primary parameter is not in chart, use its filter state from additional parameters
  if (
    paramContext.primaryReportParameterFilterId === filterId &&
    !paramContext.hasPrimaryParameterInChart
  ) {
    if (paramContext.additionalParameters.length === 0) {
      return currentFilterState;
    }

    const baseFromAdditional = paramContext.additionalParameters
      .find((p) => p.filterId === filterId)
      ?.filterMap.get(filterId);

    if (baseFromAdditional) return baseFromAdditional;

    const baseFromDefinition = getFilterDefinition(filterId);
    if (baseFromDefinition) return baseFromDefinition;
  }

  return currentFilterState;
};

/**
 * Merges multiple filter states into a base filter state.
 * Handles TREE filters (merging both selections and primarySelections)
 * and LIST/STATIC_LIST filters (merging selections only).
 * Other filter types are returned unchanged.
 */
export const mergeFilterStates = (
  base: Filter,
  additions: Filter[]
): Filter => {
  if (additions.length === 0) return base;

  return additions.reduce<Filter>((acc, extra) => {
    if (acc.type === FilterType.TREE && extra.type === FilterType.TREE) {
      return {
        ...acc,
        selections: mergeUniqueByKey(acc.selections, extra.selections),
        primarySelections: mergeUniqueByKey(
          acc.primarySelections,
          extra.primarySelections
        ),
      };
    }

    if (
      (acc.type === FilterType.LIST || acc.type === FilterType.STATIC_LIST) &&
      (extra.type === FilterType.LIST || extra.type === FilterType.STATIC_LIST)
    ) {
      return {
        ...acc,
        selections: mergeUniqueByKey(acc.selections, extra.selections),
      };
    }

    return acc;
  }, base);
};
